import { computed, ref } from 'vue'
import { getErrorMessage, logger, useKsI18n } from '@siclop/kstone-vue'
import { LeaveBalanceService } from '../services/LeaveBalanceService'
import { LeaveBalanceFilter } from '../filters/LeaveBalanceFilter'
import { LeaveBalance } from '../models/LeaveBalance'
import { isLeaveBalanceExpiringSoon, toIsoDate } from './leaveBalanceDisplay'

/**
 * Soldes non échus d'un salarié (`openAt` = aujourd'hui), en état local : lecture filtrée par salarié, donc jamais
 * dans le store partagé des soldes (qui garde la liste complète de la vue RH).
 */
export function useEmployeeLeaveBalances() {
  const { t } = useKsI18n()
  const balances = ref<LeaveBalance[]>([])
  const loading = ref(false)
  const error = ref('')

  /** Soldes triés par type puis par période (la plus ancienne d'abord : c'est elle qui expire la première). */
  const sorted = computed(() => [...balances.value].sort((a, b) =>
    (a.leaveTypeName ?? '').localeCompare(b.leaveTypeName ?? '', 'fr') || a.periodStartYear - b.periodStartYear))

  /** Soldes dont des jours restants seront perdus dans moins de 60 jours. */
  const expiringSoon = computed(() => sorted.value.filter(b => isLeaveBalanceExpiringSoon(b)))

  async function load(employeeId: number, today: Date = new Date()): Promise<void> {
    if (!employeeId) {
      balances.value = []
      return
    }
    loading.value = true
    error.value = ''
    try {
      const result = await LeaveBalanceService.list(new LeaveBalanceFilter({
        employeeId,
        openAt: toIsoDate(today),
        pageSize: -1
      }))
      balances.value = (result ?? []).map(b => new LeaveBalance(b))
    } catch (e: unknown) {
      balances.value = []
      error.value = getErrorMessage(e, t('ksproject.hr.leaveBalance.loadError', 'Erreur lors du chargement des soldes de congé'))
      logger.error('Chargement des soldes de congé du salarié', e)
    } finally {
      loading.value = false
    }
  }

  return { balances: sorted, expiringSoon, loading, error, load }
}
